import { useLayoutEffect, useMemo, useRef } from 'react';
import * as THREE from 'three';
import { ThreeEvent } from '@react-three/fiber';
import { Entity, useAppStore, useSelectedIdSet } from '../../state/store';
import { dispatchEntityClick } from '../toolEventBus';
import { ensureGeometryBVH } from './instancingUtils';

/**
 * Instanced Entity Group (task 3.1)
 *
 * Renders repetitive primitive entities (box/sphere/cylinder) with a single
 * InstancedMesh per type instead of one mesh (and draw call) per element.
 *
 * Hybrid strategy per plan: elements that are selected/hovered are excluded
 * by EntityLayer and rendered as individual meshes so they keep full
 * highlight visuals; instances handle the static bulk.
 */
export const INSTANCE_THRESHOLD = 8;

export type InstancedEntityType = 'box' | 'sphere' | 'cylinder';

// Reusable scratch objects (single-threaded render phase usage)
const _matrix = new THREE.Matrix4();
const _position = new THREE.Vector3();
const _quaternion = new THREE.Quaternion();
const _euler = new THREE.Euler();
const _scale = new THREE.Vector3();

function readInstanceTransform(type: InstancedEntityType, entity: Entity): void {
    const data = entity.data as Record<string, any>;

    switch (type) {
        case 'box': {
            const position = (data.position as [number, number, number]) || [0, 0, 0.5];
            const rotation = (data.rotation as [number, number, number]) || [0, 0, 0];
            const size = (data.size as [number, number, number]) || [1, 1, 1];
            _position.set(position[0], position[1], position[2]);
            _euler.set(rotation[0], rotation[1], rotation[2]);
            _quaternion.setFromEuler(_euler);
            _scale.set(size[0], size[1], size[2]);
            break;
        }
        case 'sphere': {
            const position = (data.position as [number, number, number]) || [0, 0, 0.5];
            const radius = (data.radius as number) || 0.5;
            _position.set(position[0], position[1], position[2]);
            _quaternion.identity();
            _scale.set(radius * 2, radius * 2, radius * 2);
            break;
        }
        case 'cylinder': {
            const position = (data.position as [number, number, number]) || [0, 0, 0.75];
            const radiusTop = (data.radiusTop as number) || 0.5;
            const radiusBottom = (data.radiusBottom as number) || 0.5;
            const height = (data.height as number) || 1.5;
            _position.set(position[0], position[1], position[2]);
            // Z-up world: unit cylinder is built along +Y; rotate onto +Z so
            // the extruded axis is the level axis (matches EntityLayer).
            _euler.set(Math.PI / 2, 0, 0);
            _quaternion.setFromEuler(_euler);
            _scale.set(radiusTop * 2, height, radiusBottom * 2);
            break;
        }
    }
}

interface InstancedEntityGroupProps {
    type: InstancedEntityType;
    entities: Entity[];
}

const DEFAULT_INSTANCE_COLOR = new THREE.Color('#808080');
const HIGHLIGHT_INSTANCE_COLOR = new THREE.Color('#00aaff');

export function InstancedEntityGroup({ type, entities }: InstancedEntityGroupProps) {
    const meshRef = useRef<THREE.InstancedMesh>(null);
    const geometryRef = useRef<any>(null);

    const ids = useMemo(() => entities.map((entity) => entity.id), [entities]);

    // Per-instance selection/hover highlighting via instanceColor (task 3.1)
    const selectedIdSet = useSelectedIdSet();
    const hoveredId = useAppStore((state) => state.hoveredId);

    // Grow-only capacity buffer (was `key={entities.length}`, remounting the
    // InstancedMesh on every membership change): R3F rebuilds only when the
    // 32-rounded capacity steps up, and mesh.count clamps rendering/raycast
    // to the active prefix.
    const capacityRef = useRef(0);
    const needed = Math.max(1, entities.length);
    if (needed > capacityRef.current) {
        capacityRef.current = Math.ceil(needed / 32) * 32;
    }
    const capacity = capacityRef.current;

    // Write instance matrices whenever the entity set changes. Split from the
    // color pass: hover writes hit hoveredId on EVERY pointer hover change,
    // and re-composing all matrices + recomputing the bounding sphere per
    // hover was the largest fan-out cost in the group.
    useLayoutEffect(() => {
        const mesh = meshRef.current;
        if (!mesh) return;

        entities.forEach((entity, index) => {
            readInstanceTransform(type, entity);
            _matrix.compose(_position, _quaternion, _scale);
            mesh.setMatrixAt(index, _matrix);
        });
        // Pre-size instanceColor for the full capacity: three's lazy
        // setColorAt allocation would size it to the current count only.
        if (!mesh.instanceColor || mesh.instanceColor.count < capacity) {
            mesh.instanceColor = new THREE.InstancedBufferAttribute(new Float32Array(capacity * 3).fill(1), 3);
        }
        mesh.count = entities.length;
        mesh.instanceMatrix.needsUpdate = true;
        mesh.computeBoundingSphere();
    }, [entities, type, capacity]);

    // Per-instance highlight colors, diffed: only instances whose highlighted
    // state actually changed get a setColorAt write (one enter + one leave per
    // hover move, instead of rewriting the whole batch's color attribute).
    const highlightRef = useRef<Map<string, boolean>>(new Map());
    const lastCapacityRef = useRef(0);
    const lastEntitiesRef = useRef<Entity[] | null>(null);
    // Diffed per-instance highlight colors: only instances whose highlighted
    // state actually changed get a setColorAt write. A capacity step remounts
    // the mesh, and a membership change shifts ids across indices — both
    // force a full re-color; a pure hover/selection change (same array) stays
    // on the O(changed)-only diff path.
    useLayoutEffect(() => {
        const mesh = meshRef.current;
        if (!mesh) return;
        if (!mesh.instanceColor || mesh.instanceColor.count < capacity) {
            mesh.instanceColor = new THREE.InstancedBufferAttribute(new Float32Array(capacity * 3).fill(1), 3);
        }
        const force = lastCapacityRef.current !== capacity || lastEntitiesRef.current !== entities;
        lastCapacityRef.current = capacity;
        lastEntitiesRef.current = entities;
        const prev = highlightRef.current;
        const next = new Map<string, boolean>();
        let changed = false;
        entities.forEach((entity, index) => {
            const highlighted = selectedIdSet.has(entity.id) || hoveredId === entity.id;
            next.set(entity.id, highlighted);
            if (force || prev.get(entity.id) !== highlighted) {
                mesh.setColorAt(index, highlighted ? HIGHLIGHT_INSTANCE_COLOR : DEFAULT_INSTANCE_COLOR);
                changed = true;
            }
        });
        highlightRef.current = next;
        if (changed && mesh.instanceColor) mesh.instanceColor.needsUpdate = true;
    }, [entities, selectedIdSet, hoveredId, capacity]);

    // BVH-accelerated raycasting on the shared unit geometry (task 3.4)
    useLayoutEffect(() => {
        if (geometryRef.current) ensureGeometryBVH(geometryRef.current);
    }, []);

    const handleClick = (event: ThreeEvent<MouseEvent>) => {
        const instanceId = event.instanceId;
        if (instanceId == null) return;
        const entityId = ids[instanceId];
        if (!entityId) return;
        event.stopPropagation();
        dispatchEntityClick(entityId, event);
    };

    return (
        <instancedMesh
            ref={meshRef}
            args={[undefined, undefined, capacity]}
            castShadow
            userData={{ instancedEntityType: type }}
            onClick={handleClick}
        >
            {type === 'box' && <boxGeometry ref={geometryRef} args={[1, 1, 1]} />}
            {type === 'sphere' && <sphereGeometry ref={geometryRef} args={[0.5, 32, 32]} />}
            {type === 'cylinder' && <cylinderGeometry ref={geometryRef} args={[0.5, 0.5, 1, 32]} />}
            <meshStandardMaterial color="#ffffff" />
        </instancedMesh>
    );
}
